/**
 * Overuren-staffel (batch-9 facturen, Deel 2) - per-dag getrapt overuren-tarief.
 *
 * Business rule (bevestigd door Jelle): reken PER DAG op het TOTAAL aantal
 * werkelijk gewerkte uren die dag, getrapt. De staffel-uren samen zijn gelijk aan
 * de gewerkte uren die dag - er komt GEEN losse overuren-regel bovenop een
 * volledige basisregel (dat was juist de dubbeltelling).
 *
 *   | Uur in de dag | Factor (van het uurtarief INCL. toeslag) |
 *   |---------------|------------------------------------------|
 *   | 1 t/m 8       | 100%                                     |
 *   | 9 en 10       | 125%                                     |
 *   | 11 en 12      | 150%                                     |
 *   | > 12          | 200%                                     |
 *
 * De factor rekent over het tarief INCL. de van toepassing zijnde toeslag
 * (ploegen-/weekendtoeslag); dat tarief geeft de caller mee. De staffel geldt
 * per dag (1 `WerkbonDayEntry`); een werkbon over meerdere dagen telt per dag
 * opnieuw vanaf uur 1.
 *
 * Conventie (consistent met `invoice-calculations.ts`): reken in volle precisie,
 * rond pas op het EIND af (per band), niet per sub-uur. De factoren 1.0/1.25/1.5/2.0
 * zijn exact in floating point, dus er gaat geen precisie verloren.
 */

const round2 = (n: number): number => Math.round(n * 100) / 100

/** Standaard overuurtarief-vermenigvuldiger (uurtarief x 1.5) - spiegelt de
 * werkbon-snapshot-default (`computeOvertimeRate`). Gebruikt om een KLANT-
 * afgesproken vast overuurtarief te herkennen (dat wijkt af van 1.5x). */
export const OVERUREN_STANDAARD_FACTOR = 1.5

/**
 * Eén band in de overuren-staffel. `totUur` = cumulatieve bovengrens van de band
 * in uren (8 = "t/m uur 8"); `null` = laatste band (oneindig). `factor` = factor
 * over het uurtarief incl. toeslag (1.0 = 100%). `label` = weergave ("125%").
 */
export interface OverurenStaffelBand {
  totUur: number | null
  factor: number
  label: string
}

/**
 * VOORBEELD-staffel (bevestigd door Jelle, Deel 2). Drempels (8/10/12) en factoren
 * centraal hier zodat ze instelbaar zijn en niet verspreid gehardcode. Een andere
 * staffel kan als argument worden meegegeven aan de functies hieronder.
 */
export const DEFAULT_OVERUREN_STAFFEL: OverurenStaffelBand[] = [
  { totUur: 8, factor: 1.0, label: '100%' },
  { totUur: 10, factor: 1.25, label: '125%' },
  { totUur: 12, factor: 1.5, label: '150%' },
  { totUur: null, factor: 2.0, label: '200%' },
]

/** Uren per band voor één dag (geen geld) - zodat meerdaagse callers de uren per
 * band kunnen sommeren VÓÓR ze afronden (rond pas op het eind af). */
export interface OverurenBandUren {
  factor: number
  label: string
  uren: number
}

/**
 * Splits de gewerkte uren van ÉÉN dag in de staffel-banden (alleen uren, geen
 * geld). De som van de band-uren is exact gelijk aan `gewerkteUren` (geen
 * dubbeltelling). Lege array bij 0 of negatieve uren.
 */
export function splitDagUrenInBanden(
  gewerkteUren: number,
  staffel: OverurenStaffelBand[] = DEFAULT_OVERUREN_STAFFEL,
): OverurenBandUren[] {
  const result: OverurenBandUren[] = []
  if (!Number.isFinite(gewerkteUren) || gewerkteUren <= 0) return result
  let ondergrens = 0
  for (const band of staffel) {
    const bovengrens = band.totUur ?? Number.POSITIVE_INFINITY
    const urenInBand = Math.min(gewerkteUren, bovengrens) - ondergrens
    if (urenInBand > 0) {
      result.push({ factor: band.factor, label: band.label, uren: urenInBand })
    }
    if (gewerkteUren <= bovengrens) break
    ondergrens = bovengrens
  }
  return result
}

/** Eén staffel-band mét bedragen (afgerond op het eind). */
export interface OverurenStaffelBandResultaat {
  factor: number
  label: string
  uren: number
  /** round2(effectiefUurtarief x factor). */
  tariefPerUur: number
  /** round2(uren x effectiefUurtarief x factor). */
  bedrag: number
}

/**
 * Volledige staffel-uitsplitsing voor ÉÉN dag, mét bedragen. `effectiefUurtarief`
 * = het uurtarief INCL. de van toepassing zijnde toeslag. Afronding op het eind
 * (per band), niet per sub-uur.
 *
 * Voorbeeld (12-uurs dag, tarief incl. toeslag EUR 50):
 *   8u x 50 (100%) + 2u x 62,50 (125%) + 2u x 75 (150%) = 675; gefactureerde uren = 12.
 */
export function berekenOverurenStaffel(
  gewerkteUren: number,
  effectiefUurtarief: number,
  staffel: OverurenStaffelBand[] = DEFAULT_OVERUREN_STAFFEL,
): OverurenStaffelBandResultaat[] {
  return splitDagUrenInBanden(gewerkteUren, staffel).map((b) => ({
    factor: b.factor,
    label: b.label,
    uren: b.uren,
    tariefPerUur: round2(effectiefUurtarief * b.factor),
    bedrag: round2(b.uren * effectiefUurtarief * b.factor),
  }))
}

/**
 * Accumuleert de staffel over MEERDERE dagen (bv. alle dagen van één medewerker
 * binnen één werkbon) en geeft per band de gesommeerde uren + bedragen terug.
 *
 * Cruciaal: de staffel wordt PER DAG toegepast (`splitDagUrenInBanden`) en de
 * band-uren worden daarna gesommeerd - NIET de dagen optellen en dan één keer
 * splitsen (dat zou twee 6-uurs dagen als één 12-uurs dag behandelen). Bedragen
 * worden pas op het eind afgerond.
 */
export function accumuleerOverurenStaffel(
  dagUren: number[],
  effectiefUurtarief: number,
  staffel: OverurenStaffelBand[] = DEFAULT_OVERUREN_STAFFEL,
): OverurenStaffelBandResultaat[] {
  const urenPerBand = new Map<number, { factor: number; label: string; uren: number }>()
  for (const uren of dagUren) {
    for (const b of splitDagUrenInBanden(uren, staffel)) {
      const cur = urenPerBand.get(b.factor) ?? { factor: b.factor, label: b.label, uren: 0 }
      cur.uren += b.uren
      urenPerBand.set(b.factor, cur)
    }
  }
  // Volgorde van de staffel behouden (100% eerst, dan 125/150/200).
  return staffel
    .filter((s) => urenPerBand.has(s.factor))
    .map((s) => {
      const agg = urenPerBand.get(s.factor)!
      return {
        factor: s.factor,
        label: s.label,
        uren: agg.uren,
        tariefPerUur: round2(effectiefUurtarief * s.factor),
        bedrag: round2(agg.uren * effectiefUurtarief * s.factor),
      }
    })
}

/** FO-305 P4 (batch-33) — uren per overwerk-trede. */
export interface OverurenTreden {
  /** Meeruren 100% (boven contracturen, geen premie). */
  p100: number
  p125: number
  p150: number
  p200: number
}

const round2Trede = (n: number): number => (Number.isFinite(n) ? Math.round(n * 100) / 100 : 0)

/**
 * FO-305 P4 (batch-33) — splits het TOTALE (week-net) aantal overuren van een
 * medewerker in de vier CAO-treden (100/125/150/200), SLUITEND: de som van de
 * treden is exact `overurenTotaal`.
 *
 * Bron van de premie-treden: de bestaande per-DAG-staffel (`accumuleerOveruren-
 * Staffel` op de werkelijk gewerkte dag-uren) - géén tweede regelset. De premie-
 * uren (125/150/200) uit die staffel worden HOOGSTE-TREDE-EERST toegewezen aan
 * het overuren-budget (de overuren zijn de laatste, "hoogste" uren van een lange
 * dag); wat daarna resteert is Meeruren 100% (boven contracturen zonder premie).
 *
 * Waarom highest-first + cappen: het payroll-`overuren` is week-net (na min-uren-
 * saldering), terwijl de staffel per dag op de bruto dag-uren rekent. De premie-
 * treden kunnen dus samen méér uren tellen dan het week-net-overuren. Door per
 * trede te cappen op het resterende overuren-budget blijft de som exact gelijk
 * aan de oude overuren-kolom (acceptatiecriterium P4) en volgt de indeling de
 * bestaande staffel. TVT is een geld-/saldo-kwestie (premie-only), geen
 * uren-splitsing - de treden tonen álle overuren-UREN.
 *
 * @param overurenTotaal week-net overuren (de oude som-kolom).
 * @param dagUren werkelijk gewerkte uren per dag (voor de per-dag-staffel).
 */
export function splitOverurenInTreden(
  overurenTotaal: number,
  dagUren: number[],
  staffel: OverurenStaffelBand[] = DEFAULT_OVERUREN_STAFFEL,
): OverurenTreden {
  const totaal = round2Trede(Math.max(0, overurenTotaal))
  if (totaal <= 0) return { p100: 0, p125: 0, p150: 0, p200: 0 }

  // Premie-uren per factor uit de per-dag-staffel.
  const banden = accumuleerOverurenStaffel(dagUren, 1, staffel)
  const urenVoorFactor = (f: number): number =>
    round2Trede(banden.find((b) => b.factor === f)?.uren ?? 0)
  const s200 = urenVoorFactor(2.0)
  const s150 = urenVoorFactor(1.5)
  const s125 = urenVoorFactor(1.25)

  // Highest-first toewijzen binnen het overuren-budget; rest = Meeruren 100%.
  let rest = totaal
  const p200 = Math.min(rest, s200)
  rest = round2Trede(rest - p200)
  const p150 = Math.min(rest, s150)
  rest = round2Trede(rest - p150)
  const p125 = Math.min(rest, s125)
  rest = round2Trede(rest - p125)
  const p100 = round2Trede(rest) // sluitpost, >= 0
  return {
    p100,
    p125: round2Trede(p125),
    p150: round2Trede(p150),
    p200: round2Trede(p200),
  }
}

/**
 * Heeft de werkbon een VAST (klant-afgesproken) overuurtarief i.p.v. het standaard
 * uurtarief x 1.5? Zo ja, dan gaat dat vaste tarief VÓÓR de staffel (Deel 2,
 * klant-override: "anders krijgen die klanten een regressie").
 *
 * Detectie via de werkbon-snapshot: het standaard-overuurtarief is
 * round2(uurtarief x 1.5); wijkt het snapshot daarvan meetbaar af, dan is er een
 * afgesproken vast tarief. (Een afgesproken tarief dat toevallig exact 1.5x is,
 * is niet te onderscheiden - dan valt de werkbon in de staffel; gedocumenteerd in
 * STATUS.)
 */
export function heeftVastOveruurtarief(uurtarief: number, overuurtarief: number): boolean {
  if (!Number.isFinite(overuurtarief) || overuurtarief <= 0) return false
  if (!Number.isFinite(uurtarief) || uurtarief <= 0) return false
  const standaard = round2(uurtarief * OVERUREN_STANDAARD_FACTOR)
  return Math.abs(overuurtarief - standaard) > 0.005
}
